import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BRAND } from "../../lib/brand";

/**
 * Shared scaffold for /terms, /privacy, /community-guidelines.
 * Single source of typography + spacing + back-link + last-updated stamp.
 *
 * Sections accept an array of { id, title, body? } where body is JSX.
 */
export default function LegalLayout({ title, lede, sections, testid }) {
    useEffect(() => {
        document.title = `${title} · ${BRAND.name}`;
    }, [title]);

    return (
        <div className="min-h-screen bg-alpine-mist text-granite" data-testid={testid}>
            <header className="bg-midnight-pine text-white">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center gap-3">
                    <Link
                        to="/"
                        data-testid="legal-back-home"
                        className="inline-flex items-center gap-2 text-sm font-bold text-white/80 hover:text-ember-orange transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to {BRAND.name}
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
                <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-ember-orange mb-3">
                    Legal · Last updated {BRAND.legal.effectiveDate}
                </div>
                <h1
                    data-testid="legal-title"
                    className="font-display font-black text-4xl sm:text-5xl text-midnight-pine tracking-tight leading-[1.05] mb-4"
                >
                    {title}
                </h1>
                {lede && (
                    <p className="text-base text-slate-gray leading-relaxed mb-10 max-w-2xl">
                        {lede}
                    </p>
                )}

                {/* Table of contents */}
                <nav
                    aria-label="Table of contents"
                    className="bg-snowcap border border-ridge-line rounded-lg p-5 mb-10"
                >
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-gray mb-3">
                        Contents
                    </div>
                    <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                        {sections.map((s, i) => (
                            <li key={s.id}>
                                <a
                                    href={`#${s.id}`}
                                    data-testid={`legal-toc-${s.id}`}
                                    className="text-granite hover:text-ember-orange font-semibold"
                                >
                                    {i + 1}. {s.title}
                                </a>
                            </li>
                        ))}
                    </ol>
                </nav>

                {/* Sections */}
                <div className="space-y-10">
                    {sections.map((s, i) => (
                        <section
                            key={s.id}
                            id={s.id}
                            data-testid={`legal-section-${s.id}`}
                            className="scroll-mt-24"
                        >
                            <h2 className="font-display font-black text-xl sm:text-2xl text-midnight-pine tracking-tight mb-3">
                                {i + 1}. {s.title}
                            </h2>
                            <div className="legal-prose text-[15px] leading-7 text-granite space-y-3">
                                {s.body}
                            </div>
                        </section>
                    ))}
                </div>

                <div className="mt-16 pt-8 border-t border-ridge-line text-xs text-slate-gray space-y-1">
                    <div>© {new Date().getFullYear()} {BRAND.legal.entity}. All rights reserved.</div>
                    <div>{BRAND.legal.mailingAddress}</div>
                    <div>
                        Questions? <a className="text-ember-orange font-semibold" href={`mailto:${BRAND.legal.contactEmail}`}>{BRAND.legal.contactEmail}</a>
                    </div>
                </div>
            </main>

            <style>{`
                .legal-prose ul { list-style: disc; padding-left: 1.25rem; }
                .legal-prose ol { list-style: decimal; padding-left: 1.25rem; }
                .legal-prose li { margin-bottom: 0.25rem; }
                .legal-prose strong { color: #17382F; }
                .legal-prose a { color: #F26A2E; font-weight: 600; }
                .legal-prose a:hover { text-decoration: underline; }
                .legal-prose h3 { font-weight: 800; color: #17382F; margin: 1rem 0 0.5rem 0; font-size: 1rem; }
            `}</style>
        </div>
    );
}
